import React, { useEffect, useMemo, useState } from "react";
import Modal from "../design-system/components/modals/Modal";
import { PrimaryButton } from "../design-system/components/buttons";
import { apiFetch } from "../core/utils/api";
import { typography, textColors } from "../design-system/tokens";
import SubscriptionPlansSection from "./components/SubscriptionPlansSection";
import GlobalDiscountBanner from "./components/GlobalDiscountBanner";
import FeatureComparison from "./components/FeatureComparison";
import { buildCouponAppliedLabel, buildCouponStateFromPreview, getFriendlyCouponErrorMessage, pickBestCouponError } from "./utils/couponUtils";
import SubscriptionPaymentModal from "./components/SubscriptionPaymentModal";
import SubscriptionUnavailableModal from "./components/SubscriptionUnavailableModal";
import { clearReferralCoupon, getReferralCoupon, setReferralCoupon } from "../core/utils/referralCoupon";
import { submitPotentialCustomerLead } from "../core/utils/leadsApi";
import { getNewSubscriptionsAllowedSafe } from "../core/utils/subscriptionConfigApi";
import ConfettiOverlay from "../design-system/components/ConfettiOverlay";

const API_ENDPOINTS = {
  CURRENT_SUBSCRIPTION: "/api/subscription/current/",
  PLANS: "/api/subscription/plans/",
  GLOBAL_DISCOUNT_ACTIVE: "/api/global-discount/active/",
  STRIPE_SUBSCRIPTION_START: "/api/stripe/subscription/start/",
  STRIPE_CHANGE_NOW: "/api/stripe/subscription/change/",
  COUPON_PREVIEW: "/api/coupons/preview/",
  FREE_ACCESS_COUPON_REDEEM: "/api/coupons/free-access/redeem/",
};

const formatDateShort = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const SubscriptionPlansModal = ({
  isOpen,
  onClose,
  notice, // optional ReactNode shown at top (e.g. publish gating message)
  showFeatureComparison = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showAnnualPricing, setShowAnnualPricing] = useState(true);

  const [globalDiscount, setGlobalDiscount] = useState(null);
  const [globalDiscountError, setGlobalDiscountError] = useState(null);

  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponState, setCouponState] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  // Stacking model: global discount (if eligible) always stacks with the entered coupon (if eligible).

  const [actionLoading, setActionLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null); // { planName, billingPeriod, couponCode }
  const autoApplyAttemptedRef = React.useRef(false);
  const [subscriptionUnavailableModalOpen, setSubscriptionUnavailableModalOpen] = useState(false);

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successPlan, setSuccessPlan] = useState(null); // { planName, billingPeriod }
  const [showConfetti, setShowConfetti] = useState(false);
  const [freeAccessModalOpen, setFreeAccessModalOpen] = useState(false);
  const [freeAccessGrant, setFreeAccessGrant] = useState(null); // { code, plan_name, ends_at }

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    autoApplyAttemptedRef.current = false;
    setSubscriptionUnavailableModalOpen(false);
    setSuccessModalOpen(false);
    setSuccessPlan(null);
    setShowConfetti(false);

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [subRes, plansRes] = await Promise.all([
          apiFetch(API_ENDPOINTS.CURRENT_SUBSCRIPTION, { headers: { "Content-Type": "application/json" } }),
          apiFetch(API_ENDPOINTS.PLANS, { headers: { "Content-Type": "application/json" } }),
        ]);
        const subData = await subRes.json().catch(() => ({}));
        const plansData = await plansRes.json().catch(() => ({}));
        if (!subRes.ok) throw new Error(subData?.error || "Failed to load subscription.");
        if (!plansRes.ok) throw new Error(plansData?.error || "Failed to load plans.");
        if (!cancelled) {
          setCurrentSubscription(subData);
          setPlans(plansData?.plans || []);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load subscription plans.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const loadGlobal = async () => {
      setGlobalDiscountError(null);
      try {
        const resp = await apiFetch(API_ENDPOINTS.GLOBAL_DISCOUNT_ACTIVE, { headers: { "Content-Type": "application/json" } });
        const data = await resp.json().catch(() => null);
        if (!resp.ok) throw new Error(data?.error || "Failed to load global discount.");
        if (!cancelled) setGlobalDiscount(data);
      } catch (e) {
        if (!cancelled) {
          setGlobalDiscount(null);
          setGlobalDiscountError(e?.message || "Failed to load global discount.");
        }
      }
    };

    load();
    loadGlobal();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const currentPlan = useMemo(
    () => (plans || []).find((p) => p.name === currentSubscription?.plan),
    [plans, currentSubscription?.plan]
  );

  const targetBillingPeriod = showAnnualPricing ? "yearly" : "monthly";
  const isPaidSubscription = (currentSubscription?.plan || "free") !== "free" && Number(currentSubscription?.price || 0) > 0;
  const shouldShowGlobalDiscount = !!globalDiscount && !(globalDiscount?.first_time_only && isPaidSubscription);
  const couponCode = couponState?.code && !couponState?.is_access_coupon ? couponState.code : null;
  const hasAccessGrant = Boolean(currentSubscription?.access_grant?.is_active);
  const upcomingSubscription = Array.isArray(currentSubscription?.upcoming_subscriptions) && currentSubscription.upcoming_subscriptions.length > 0
    ? currentSubscription.upcoming_subscriptions[0]
    : null;

  const getGlobalDiscountForPlan = (plan, billingPeriod) => {
    if (!shouldShowGlobalDiscount) return null;
    const period = billingPeriod || targetBillingPeriod;
    const allowedPlans = Array.isArray(globalDiscount?.plans) ? globalDiscount.plans : [];
    const allowedPeriods = Array.isArray(globalDiscount?.billing_periods) ? globalDiscount.billing_periods : [];
    if (allowedPlans.length > 0 && !allowedPlans.includes(plan.name)) return null;
    if (allowedPeriods.length > 0 && !allowedPeriods.includes(period)) return null;

    return {
      source: "global",
      name: globalDiscount?.name || "Global discount",
      percent_off: globalDiscount?.percent_off ?? null,
      amount_off: globalDiscount?.amount_off ?? null,
      free_trial_days: Number(globalDiscount?.free_trial_days || 0),
      include_renewals: globalDiscount?.include_renewals ?? null,
    };
  };

  const getCouponDiscountForPlan = (plan, billingPeriod) => {
    if (!couponState?.code || couponState?.is_access_coupon) return null;
    const period = billingPeriod || targetBillingPeriod;
    const source = couponState?.byPeriod?.[period];
    const match = (source?.plans || []).find((p) => p.plan === plan.name && p.billing_period === period);
    if (!match) return null;
    return {
      source: "coupon",
      name: couponState?.code || "Coupon",
      percent_off: couponState?.percent_off ?? null,
      amount_off: couponState?.amount_off ?? null,
      free_trial_days: Number(couponState?.free_trial_days || 0),
      include_renewals: couponState?.include_renewals ?? null,
    };
  };

  const pickDisplayDiscount = (plan, billingPeriod) => {
    const period = billingPeriod || targetBillingPeriod;
    const discounts = [];
    const gd = getGlobalDiscountForPlan(plan, period);
    const cd = getCouponDiscountForPlan(plan, period);
    if (gd) discounts.push(gd);
    if (cd) discounts.push(cd);
    if (!discounts.length) return null;
    return { discounts };
  };

  const applyCoupon = async () => {
    const code = (couponInput || "").trim();
    if (!code) return;
    setCouponLoading(true);
    setCouponError(null);
    try {
      // Free-access coupons (affiliate coupons granting free plan time) use their own endpoint.
      const freeAccessResp = await apiFetch(API_ENDPOINTS.FREE_ACCESS_COUPON_REDEEM, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const freeAccessData = await freeAccessResp.json().catch(() => ({}));
      if (freeAccessResp.ok) {
        // Refresh subscription so the grant is reflected, then show the access success state.
        const subRes = await apiFetch(API_ENDPOINTS.CURRENT_SUBSCRIPTION, { headers: { "Content-Type": "application/json" } });
        const subData = await subRes.json().catch(() => ({}));
        if (subRes.ok) setCurrentSubscription(subData);
        setCouponState({
          code,
          is_access_coupon: true,
          include_renewals: null,
          free_trial_days: 0,
          byPeriod: { monthly: null, yearly: null },
        });
        setFreeAccessGrant(freeAccessData?.access_grant || { code });
        setFreeAccessModalOpen(true);
        // Access coupons can be linked to a regular coupon; store it so it
        // auto-applies at checkout later (same idea as a ref_coupon signup).
        const linkedCode = freeAccessData?.access_grant?.linked_coupon_code;
        if (linkedCode) setReferralCoupon({ code: linkedCode });
        return;
      }
      const freeAccessError = freeAccessData?.error || "Failed to redeem coupon.";
      const freeAccessErrorLower = String(freeAccessError || "").toLowerCase();
      const allowFreeAccessFallback =
        freeAccessErrorLower.includes("coupon does not grant free access") ||
        freeAccessErrorLower.includes("invalid coupon code");
      if (!allowFreeAccessFallback) {
        throw new Error(freeAccessError);
      }

      const previewFor = async (billingPeriod) => {
        const resp = await apiFetch(API_ENDPOINTS.COUPON_PREVIEW, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, billing_period: billingPeriod }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.error || "Failed to validate coupon.");
        if (!data.valid) return { valid: false, error: data?.error || "Coupon is not available." };
        return { valid: true, data };
      };

      // Fetch both periods so switching Monthly/Annual keeps discounts consistent.
      const [monthly, yearly] = await Promise.all([previewFor("monthly"), previewFor("yearly")]);
      const monthlyValid = !!monthly?.valid;
      const yearlyValid = !!yearly?.valid;
      if (!monthlyValid && !yearlyValid) {
        const raw = pickBestCouponError(monthly?.error, yearly?.error);
        throw new Error(getFriendlyCouponErrorMessage(raw));
      }

      setCouponState(buildCouponStateFromPreview({ monthly, yearly }));
    } catch (e) {
      setCouponState(null);
      setCouponError(getFriendlyCouponErrorMessage(e?.message));
    } finally {
      setCouponLoading(false);
    }
  };

  const clearCoupon = () => {
    setCouponInput("");
    setCouponState(null);
    setCouponError(null);
    setCouponOpen(false);
    clearReferralCoupon();
  };

  // Auto-apply referral coupon (silent) once per open.
  useEffect(() => {
    if (!isOpen) return;
    if (autoApplyAttemptedRef.current) return;
    if (couponState?.code) return;
    const ref = getReferralCoupon();
    if (!ref?.code) return;

    autoApplyAttemptedRef.current = true;
    setCouponInput(ref.code);

    // Silent apply: if invalid (e.g., first_time_only), hide silently.
    const run = async () => {
      try {
        const previewFor = async (billingPeriod) => {
          const resp = await apiFetch(API_ENDPOINTS.COUPON_PREVIEW, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code: ref.code, billing_period: billingPeriod }),
          });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok) throw new Error(data?.error || "Failed to validate coupon.");
          if (!data.valid) return { valid: false, error: data?.error || "Coupon is not available." };
          return { valid: true, data };
        };

        const [monthly, yearly] = await Promise.all([previewFor("monthly"), previewFor("yearly")]);
        const monthlyValid = !!monthly?.valid;
        const yearlyValid = !!yearly?.valid;
        if (!monthlyValid && !yearlyValid) return;
        setCouponState(buildCouponStateFromPreview({ monthly, yearly }));
      } catch {
        // silent
      }
    };
    run();
  }, [isOpen, couponState?.code]);

  const couponAppliedMessage = couponState?.is_access_coupon
    ? `Access coupon applied · ${couponState.code}`
    : buildCouponAppliedLabel({
        code: couponState?.code,
        hasMonthly: !!couponState?.byPeriod?.monthly,
        hasYearly: !!couponState?.byPeriod?.yearly,
        freeTrialDays: couponState?.free_trial_days,
      });

  const openInNewTab = (url) => {
    const win = window.open(url, "_blank");
    if (win) {
      try {
        win.opener = null;
      } catch {}
      try {
        win.focus?.();
      } catch {}
      return;
    }
    window.location.assign(url);
  };

  const handleSelectPlan = async (plan) => {
    setActionLoading(true);
    setError(null);
    try {
      const allowed = await getNewSubscriptionsAllowedSafe();
      if (!allowed) {
        const info = [
          "source=subscription_plans_modal",
          plan?.name ? `target_plan=${String(plan.name)}` : null,
          targetBillingPeriod ? `billing_period=${String(targetBillingPeriod)}` : null,
        ]
          .filter(Boolean)
          .join("; ");

        try {
          void submitPotentialCustomerLead({ situation: "SUBSCRIPTION_UPGRADE_INTEREST", info });
        } catch {
          // ignore
        }

        // Close any in-flight payment modal and show the friendly availability modal instead.
        setPaymentModalOpen(false);
        setPaymentTarget(null);
        setSubscriptionUnavailableModalOpen(true);
        return;
      }

      const returnUrl = window.location.href;
      // Access grants (access coupons / admin grants) provide paid features without a Stripe subscription.
      // For upgrades, treat these users like "free" so they can start a new paid subscription.
      const isFree = (currentSubscription?.plan || "free") === "free" || hasAccessGrant;
      const currentOrder = Number(currentPlan?.order ?? 0);
      const targetOrder = Number(plan?.order ?? 0);

      if (!isFree) {
        // Downgrades are scheduled; keep that on Subscription Settings for now.
        if (targetOrder < currentOrder) {
          throw new Error("Downgrades aren’t available here.");
        }

        // Upgrade now (Stripe may return a hosted invoice URL)
        const resp = await apiFetch(API_ENDPOINTS.STRIPE_CHANGE_NOW, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            plan_name: plan.name,
            billing_period: targetBillingPeriod,
            coupon_code: couponCode,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.error || "Failed to change plan.");
        if (data?.url) {
          onClose?.();
          openInNewTab(data.url);
          return;
        }
        // Refresh subscription state, then show success celebration
        const subRes = await apiFetch(API_ENDPOINTS.CURRENT_SUBSCRIPTION, { headers: { "Content-Type": "application/json" } });
        const subData = await subRes.json().catch(() => ({}));
        if (subRes.ok) setCurrentSubscription(subData);
        setSuccessPlan({ planName: plan.name, billingPeriod: targetBillingPeriod });
        setSuccessModalOpen(true);
        setShowConfetti(true);
        return;
      }
      // Free -> paid: in-app payment modal using Stripe Elements (no Checkout redirect)
      setPaymentTarget({ planName: plan.name, billingPeriod: targetBillingPeriod, couponCode });
      setPaymentModalOpen(true);
    } catch (e) {
      const msg = e?.message || "Failed to start checkout.";
      // Safety fallback: backend returns this when the user has no Stripe subscription yet.
      // In that case, route them through the payment/start flow instead of surfacing an error.
      if (String(msg).toLowerCase().includes("no stripe subscription yet")) {
        setError(null);
        setPaymentTarget({ planName: plan.name, billingPeriod: targetBillingPeriod, couponCode });
        setPaymentModalOpen(true);
        return;
      }
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <SubscriptionUnavailableModal
        isOpen={isOpen && subscriptionUnavailableModalOpen}
        onClose={() => {
          setSubscriptionUnavailableModalOpen(false);
          // Close the upgrade modal flow entirely.
          onClose?.();
        }}
      />

      <Modal
        isOpen={isOpen && !subscriptionUnavailableModalOpen && !successModalOpen && !freeAccessModalOpen}
        onClose={() => {
          if (!actionLoading) onClose?.();
        }}
        title="Upgrade your plan"
        size="xl"
        bodyMaxHeight="calc(100vh - 120px)"
        closeOnOverlayClick={!actionLoading}
        closeOnEsc={!actionLoading}
        showCloseButton={!actionLoading}
      >
        <div className="space-y-4">
          <SubscriptionPaymentModal
            isOpen={paymentModalOpen}
            onClose={() => {
              setPaymentModalOpen(false);
              setPaymentTarget(null);
            }}
            planName={paymentTarget?.planName}
            billingPeriod={paymentTarget?.billingPeriod}
            couponCode={paymentTarget?.couponCode}
            onSubscriptionUpdated={(sub) => {
              setCurrentSubscription(sub);
            }}
            onActivated={({ planName: pn, billingPeriod: bp }) => {
              setPaymentModalOpen(false);
              setPaymentTarget(null);
              setSuccessPlan({ planName: pn, billingPeriod: bp });
              setSuccessModalOpen(true);
              setShowConfetti(true);
            }}
          />

          {notice ? <div>{notice}</div> : null}

          {globalDiscountError ? (
            <div className={`${typography.caption} text-red-600 dark:text-red-400`}>{globalDiscountError}</div>
          ) : null}

          {loading ? (
            <div className={`${typography.bodySmall} ${textColors.secondary}`}>Loading…</div>
          ) : error ? (
            <div className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>{error}</div>
          ) : (
            <div className="space-y-6">
              <SubscriptionPlansSection
                currentSubscription={currentSubscription}
                currentPlan={currentPlan}
                plans={plans}
                showAnnualPricing={showAnnualPricing}
                setShowAnnualPricing={setShowAnnualPricing}
                onSelectPlan={handleSelectPlan}
                currentSubscriptionFirst={false}
                couponUI={{
                  isOpen: couponOpen,
                  toggleLink: () => setCouponOpen(!couponOpen),
                  value: couponInput,
                  setValue: (v) => {
                    setCouponInput(v);
                    if (couponError) setCouponError(null);
                  },
                  apply: applyCoupon,
                  clear: couponState ? clearCoupon : null,
                  loading: couponLoading,
                  message: couponAppliedMessage,
                  error: couponError,
                  getDisplayPrice: (p, billingPeriod) => pickDisplayDiscount(p, billingPeriod),
                  couponCode: couponState?.code || null,
                  hasMonthly: !!couponState?.byPeriod?.monthly,
                  hasYearly: !!couponState?.byPeriod?.yearly,
                  renewalApplies: couponState?.include_renewals ?? null,
                }}
                banner={
                  shouldShowGlobalDiscount ? (
                    <GlobalDiscountBanner
                      discount={globalDiscount}
                      targetBillingPeriod={targetBillingPeriod}
                      currentSubscription={currentSubscription}
                    />
                  ) : null
                }
                accessGrant={currentSubscription?.access_grant || null}
                upcomingSubscription={upcomingSubscription}
              />

              {showFeatureComparison ? <FeatureComparison plans={plans} /> : null}
            </div>
          )}
        </div>
      </Modal>

      <ConfettiOverlay isActive={showConfetti} />

      <Modal
        isOpen={isOpen && freeAccessModalOpen}
        onClose={() => {
          setFreeAccessModalOpen(false);
          onClose?.();
        }}
        title="Free access activated"
        size="sm"
        closeOnOverlayClick
        closeOnEsc
        showCloseButton
        footer={
          <PrimaryButton
            fullWidth
            onClick={() => {
              setFreeAccessModalOpen(false);
              onClose?.();
            }}
          >
            Got it
          </PrimaryButton>
        }
      >
        <div className="space-y-3">
          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
            You now have access to{" "}
            <span className={`${textColors.primary} font-medium`}>
              {(freeAccessGrant?.plan_name || "").toUpperCase() || "your plan"}
            </span>{" "}
            without adding a payment method.
          </div>
          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
            Access ends on{" "}
            <span className={`${textColors.primary} font-medium`}>
              {formatDateShort(freeAccessGrant?.ends_at) || "—"}
            </span>
            .
          </div>
          {freeAccessGrant?.code ? (
            <div className={`${typography.caption} ${textColors.tertiary}`}>Code: {freeAccessGrant.code}</div>
          ) : null}
        </div>
      </Modal>

      <Modal
        isOpen={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          setShowConfetti(false);
          onClose?.();
        }}
        title="You're all set!"
        size="md"
        closeOnOverlayClick
        closeOnEsc
        showCloseButton
      >
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-green-400 text-white flex items-center justify-center">
              <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          <div className="space-y-2">
            <p className={`${typography.body} ${textColors.primary}`}>
              Your <span className="font-semibold">{(successPlan?.planName || "").toUpperCase()}</span>{" "}
              {successPlan?.billingPeriod === "yearly" ? "(Annual)" : "(Monthly)"} plan is now active!
            </p>
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>
              Enjoy your upgraded features. You can manage your subscription anytime from your account settings.
            </p>
          </div>

          <PrimaryButton
            fullWidth
            onClick={() => {
              setSuccessModalOpen(false);
              setShowConfetti(false);
              onClose?.();
            }}
          >
            Done
          </PrimaryButton>
        </div>
      </Modal>
    </>
  );
};

export default SubscriptionPlansModal;


